import { Alert, Button, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../axios/axios-instance";
import { pathAPI } from "../../../utils/pathApi";

type WebhookClientStatus = "ACTIVE" | "REVOKED";

interface WebhookClient {
  id: number;
  name: string;
  token_prefix: string;
  status: WebhookClientStatus;
  created_date: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  token?: string;
}

interface CreateFormValues {
  name: string;
}

const NAME_MAX_LENGTH = 255;

const unwrap = <T,>(response: { data?: { data?: T } & T }): T =>
  (response?.data?.data ?? response?.data) as T;

const formatDate = (value: string | null) => (value ? value.replace("T", " ").slice(0, 19) : "-");

const WebhookClients = () => {
  const [form] = Form.useForm<CreateFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [clients, setClients] = useState<WebhookClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [issuedToken, setIssuedToken] = useState<WebhookClient | null>(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(pathAPI.webhookClients);
      setClients(unwrap<WebhookClient[]>(response) ?? []);
    } catch (error) {
      messageApi.error("Failed to load webhook connections");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const createClient = async (values: CreateFormValues) => {
    setCreating(true);
    try {
      const response = await axiosInstance.post(pathAPI.webhookClients, values);
      setIssuedToken(unwrap<WebhookClient>(response));
      setCreateOpen(false);
      form.resetFields();
      fetchClients();
    } catch (error) {
      messageApi.error("Failed to create webhook connection");
    } finally {
      setCreating(false);
    }
  };

  const revokeClient = async (id: number) => {
    try {
      await axiosInstance.put(`${pathAPI.webhookClients}/${id}/revoke`);
      messageApi.success("Webhook connection revoked");
      fetchClients();
    } catch (error) {
      messageApi.error("Failed to revoke webhook connection");
    }
  };

  const columns: ColumnsType<WebhookClient> = [
    { title: "Name", dataIndex: "name" },
    {
      title: "Token",
      dataIndex: "token_prefix",
      render: (prefix: string) => <code>{prefix}…</code>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: WebhookClientStatus) => (
        <Tag color={status === "ACTIVE" ? "green" : "default"}>{status}</Tag>
      ),
    },
    { title: "Created", dataIndex: "created_date", render: formatDate },
    { title: "Last used", dataIndex: "last_used_at", render: formatDate },
    {
      title: "",
      key: "actions",
      render: (_, client) =>
        client.status === "ACTIVE" ? (
          <Popconfirm
            title="Revoke this connection?"
            description="Systems using this token will get 401 immediately."
            okText="Revoke"
            okButtonProps={{ danger: true }}
            onConfirm={() => revokeClient(client.id)}
          >
            <Button danger size="small">
              Revoke
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 8 }}>
      {contextHolder}
      <Alert
        type="info"
        showIcon
        message="Each connection gets its own token for /api/webhook/**. Paste it into the AI tool's auth header (Authorization: Bearer or X-Webhook-Token). The token is shown only once."
      />
      <Space>
        <Button type="primary" onClick={() => setCreateOpen(true)}>
          Create connection
        </Button>
        <Button onClick={fetchClients} loading={loading}>
          Reload
        </Button>
      </Space>
      <Table<WebhookClient> rowKey="id" columns={columns} dataSource={clients} loading={loading} pagination={false} />

      <Modal
        title="Create webhook connection"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        okText="Create"
        confirmLoading={creating}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={createClient}>
          <Form.Item
            name="name"
            label="Name"
            extra="Which system will use this token, e.g. AI agent (production)."
            rules={[{ required: true, whitespace: true, message: "Name is required" }]}
          >
            <Input maxLength={NAME_MAX_LENGTH} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Copy your token now"
        open={issuedToken !== null}
        onCancel={() => setIssuedToken(null)}
        footer={<Button type="primary" onClick={() => setIssuedToken(null)}>Done</Button>}
        maskClosable={false}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            message="This token will not be shown again. If you lose it, revoke this connection and create a new one."
          />
          <Typography.Text type="secondary">{issuedToken?.name}</Typography.Text>
          <Typography.Paragraph copyable code style={{ wordBreak: "break-all", marginBottom: 0 }}>
            {issuedToken?.token}
          </Typography.Paragraph>
        </Space>
      </Modal>
    </Space>
  );
};

export default WebhookClients;
